import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { Zap, Film, PanelRightClose, PanelRightOpen, X } from 'lucide-react';

import { useCategories } from '../../../contexts/CategoriesContext';
import { saveQuickInferenceFrameSegmentations } from '../../../helpers/api/quick-inference/quick-inference';
import type {
  QuickInferenceDetail,
  QuickInferenceFrame,
  QuickInferenceSegmentation,
} from '../../../helpers/api/quick-inference/quick-inference';
import {
  convertSegmentationToRawShape,
  convertShapesToSegmentations,
  ensureSegmentationIds,
  backendLabelToDisplayName,
} from '../../Components/GeneralViewer/GeneralViewerInstanceDetail';
import { dedupeSegmentationsForLoad } from '../../ComponnentsUI/DrawingCanvas/utils/segmentationSaveUtils';
import { QuickInferenceDrawingCanvas } from './QuickInferenceDrawingCanvas';
import type { DrawingCanvasRef, ImageInput } from './QuickInferenceDrawingCanvas';
import type { Shape } from '../../ComponnentsUI/DrawingCanvas/types';
import { rawShapeToShape } from '../../ComponnentsUI/DrawingCanvas/utils/pointsConverter';
import { QuickInferenceAssignViewBar } from './QuickInferenceAssignViewBar';
import { QuickInferenceCalibrationBar } from './QuickInferenceCalibrationBar';
import { QuickInferenceMeasurementsPanel } from './QuickInferenceMeasurementsPanel';
import { QuickInferenceQualityPanel } from './QuickInferenceQualityPanel';
import { QuickInferenceViewerFilter } from './QuickInferenceViewerFilter';
import { useFrameImage } from './useFrameImage';
import {
  EMPTY_PLAYGROUND_FILTER,
  displayedFrameViews,
  expandQiBiometrySegmentationsForSave,
  formatViewLabel,
  type PlaygroundFilterOption,
  type PlaygroundViewerFilter,
} from './quickInferenceUtils';
import { useFilterOptionsOptional } from '../../../contexts/FilterOptionsContext';
import { getMeasurementDisplayName } from '../../../constants/measurementLabels';
import { useCategoryLabels } from './useCategoryLabels';

export interface FrameEntry {
  index: string;
  frameNumber: number;
  frame: QuickInferenceFrame;
}

function resolveSegmentationDisplayLabel(
  seg: QuickInferenceSegmentation,
  labelForCategoryId: (id: number) => string
): string {
  const catId = seg.category_id != null ? Number(seg.category_id) : NaN;
  if (Number.isFinite(catId)) {
    const name = labelForCategoryId(catId);
    if (name && !/^#\d+$/.test(name)) return name;
  }

  const raw = String(seg.label ?? '').trim();
  if (raw.startsWith('MEAS__')) {
    return getMeasurementDisplayName(raw.split(' ')[0]);
  }
  if (raw && !/^\d+$/.test(raw)) {
    return backendLabelToDisplayName(raw);
  }
  return 'Structure';
}

function segmentationsToShapes(
  segmentations: QuickInferenceSegmentation[],
  frameKey: string,
  getColorForCategoryId: (id: number | null | undefined) => string,
  getColorForCode: (code: string | null | undefined) => string,
  labelForCategoryId: (id: number) => string,
  measLabelToCategoryId: Record<string, number>,
  categoryIdToMeasLabel: Record<number, string>,
  categoryIdsWithMaxTwo: Set<number>
): Shape[] {
  // Biometry is stored as structure + MEAS__* (same geometry). Show one shape like General Viewer.
  const withIds = ensureSegmentationIds(
    dedupeSegmentationsForLoad(
      segmentations,
      categoryIdsWithMaxTwo,
      measLabelToCategoryId,
      categoryIdToMeasLabel
    ) as QuickInferenceSegmentation[]
  );
  const shapes: Shape[] = [];
  for (const seg of withIds) {
    const raw = convertSegmentationToRawShape(seg, frameKey);
    if (!raw) continue;
    try {
      const shape = rawShapeToShape(raw);
      const displayLabel = resolveSegmentationDisplayLabel(seg, labelForCategoryId);
      shapes.push({
        ...shape,
        label: displayLabel,
        color:
          seg.category_id != null
            ? getColorForCategoryId(seg.category_id)
            : getColorForCode(seg.label ?? undefined),
        dbId: seg.id,
        backendLabel: seg.label,
        backendType: seg.geometry_type ?? seg.type,
        ...(seg.category_id != null && { categoryId: seg.category_id }),
        isManual: seg.is_manual === true,
      } as Shape);
    } catch {
      // skip malformed
    }
  }
  return shapes;
}

interface Props {
  entry: FrameEntry;
  detail: QuickInferenceDetail;
  editable: boolean;
  onSaved: () => void | Promise<void>;
  onCalibrated?: () => void | Promise<void>;
  totalFrameCount?: number;
  globalCurrentFrameIndex?: number;
  onGlobalFrameIndexChange?: (oneBasedIndex: number) => void;
  frameStrip?: React.ReactNode;
  framesPanelOpen?: boolean;
  onFramesPanelToggle?: () => void;
  showFramesToggle?: boolean;
  viewerFilter?: PlaygroundViewerFilter;
  onViewerFilterChange?: (next: PlaygroundViewerFilter) => void;
  filterViewOptions?: PlaygroundFilterOption[];
  filterStructureOptions?: PlaygroundFilterOption[];
  matchedFrameCount?: number;
  totalUnfilteredFrameCount?: number;
  hiddenCategoryIds?: number[];
  onToggleCategoryVisibility?: (categoryId: number) => void;
  emptyFilterMessage?: boolean;
  onClearFilter?: () => void;
}

export function FrameEditorPanel({
  entry,
  detail,
  editable,
  onSaved,
  onCalibrated,
  totalFrameCount,
  globalCurrentFrameIndex,
  onGlobalFrameIndexChange,
  frameStrip,
  framesPanelOpen = false,
  onFramesPanelToggle,
  showFramesToggle = false,
  viewerFilter,
  onViewerFilterChange,
  filterViewOptions = [],
  filterStructureOptions = [],
  matchedFrameCount,
  totalUnfilteredFrameCount,
  hiddenCategoryIds = [],
  onToggleCategoryVisibility,
  emptyFilterMessage = false,
  onClearFilter,
}: Props) {
  const {
    codeById,
    categoryIdToMeasLabel,
    measLabelToCategoryId,
    categoryIdsWithMaxTwo,
    getColorForCategoryId,
    getColorForCode,
  } = useCategories();
  const { labelForCategoryId } = useCategoryLabels();
  const drawingCanvasRef = useRef<DrawingCanvasRef>(null);
  const [saving, setSaving] = useState(false);
  const [activeViewDefId, setActiveViewDefId] = useState<number | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const filterOptions = useFilterOptionsOptional();

  const { url, loading, failed } = useFrameImage(detail.id, entry.frameNumber);

  const views = displayedFrameViews(entry.frame);
  const frameKey = entry.index;
  const imageId = `qi_${detail.id}_${frameKey}`;
  const hasQualityViews = views.length > 0;

  const activeViewCode = useMemo(() => {
    if (activeViewDefId == null) return undefined;
    const def = filterOptions?.views?.find((v) => v.id === activeViewDefId);
    return def?.view_code;
  }, [activeViewDefId, filterOptions?.views]);

  const primaryViewLabel = useMemo(() => {
    const code = activeViewCode ?? views[0];
    if (!code) return null;
    const def = filterOptions?.views?.find((v) => v.view_code === code);
    return def?.display_name || formatViewLabel(code);
  }, [activeViewCode, views, filterOptions?.views]);

  useEffect(() => {
    setActiveViewDefId(null);
  }, [detail.id, entry.frameNumber]);

  const pixelSpacingMm = useMemo(() => {
    const ps = detail.pixel_spacing ?? detail.result?.metadata?.pixel_spacing;
    if (ps == null || ps <= 0) return null;
    return ps;
  }, [detail]);

  const pixelSpacing = useMemo(() => {
    if (pixelSpacingMm == null) return undefined;
    return { x: pixelSpacingMm, y: pixelSpacingMm };
  }, [pixelSpacingMm]);

  const needsCalibration =
    detail.status === 'success' && (detail.pixel_spacing == null || detail.pixel_spacing <= 0);

  // Avoid a tall blank criteria column when the frame has nothing to show.
  useEffect(() => {
    setSidePanelOpen(hasQualityViews || needsCalibration);
  }, [detail.id, entry.frameNumber, hasQualityViews, needsCalibration]);

  const initialShapes = useMemo(
    () =>
      segmentationsToShapes(
        entry.frame.segmentations || [],
        frameKey,
        getColorForCategoryId,
        getColorForCode,
        labelForCategoryId,
        measLabelToCategoryId,
        categoryIdToMeasLabel,
        categoryIdsWithMaxTwo
      ),
    [
      entry.frame.segmentations,
      frameKey,
      getColorForCategoryId,
      getColorForCode,
      labelForCategoryId,
      measLabelToCategoryId,
      categoryIdToMeasLabel,
      categoryIdsWithMaxTwo,
    ]
  );

  const images: ImageInput[] = useMemo(() => {
    if (!url) return [];
    return [
      {
        id: imageId,
        name: `Frame ${entry.frameNumber}`,
        src: url,
        pixelSpacing,
        initialShapes,
      },
    ];
  }, [url, imageId, entry.frameNumber, pixelSpacing, initialShapes]);

  const handleSave = useCallback(
    async (shapes: Shape[], _imageId: string, _imageName: string, deletedDbIds: (number | string)[]) => {
      if (!editable) {
        message.warning('Editing is disabled while inference is in progress');
        return;
      }

      const changedShapes = shapes.filter((s: any) => s.isNew || s.isDirty);
      if (changedShapes.length === 0 && deletedDbIds.length === 0) {
        message.info('No changes to save');
        return;
      }

      const segmentations = expandQiBiometrySegmentationsForSave(
        changedShapes,
        convertShapesToSegmentations(
          changedShapes,
          pixelSpacing,
          codeById,
          categoryIdToMeasLabel
        )
      ).filter((s) => s.points);

      setSaving(true);
      try {
        const saved = await saveQuickInferenceFrameSegmentations(detail.id, frameKey, {
          segmentations,
          delete_segmentation_ids: deletedDbIds,
        });
        const removed = saved?.removed_views || [];
        if (removed.length > 0) {
          message.warning(
            `Views cleared or reduced — structures no longer match: ${removed
              .map((c) => formatViewLabel(c))
              .join(', ')}`
          );
        } else {
          message.success('Segmentations saved');
        }
        drawingCanvasRef.current?.clearDirtyStates();
        drawingCanvasRef.current?.clearDeletedDbIds?.();
        await onSaved();
      } catch (err: any) {
        message.error(err?.response?.data?.detail || err?.detail || err?.message || 'Failed to save');
      } finally {
        setSaving(false);
      }
    },
    [
      editable,
      pixelSpacing,
      codeById,
      categoryIdToMeasLabel,
      detail.id,
      frameKey,
      onSaved,
    ]
  );

  const handleCalibrated = useCallback(async () => {
    await onCalibrated?.();
  }, [onCalibrated]);

  const canvasLoading = loading;
  const canvasFailed = failed || !url;
  const canvasReady = Boolean(url && images.length > 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 lg:flex-row">
      <div className="flex min-h-[min(55vh,520px)] min-w-0 flex-1 flex-col lg:min-h-0">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-gray-200 px-2 py-2 dark:border-gray-700 sm:px-3">
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 sm:text-xs">
              frame {globalCurrentFrameIndex ?? entry.frameNumber + 1}
              {totalFrameCount ? ` of ${totalFrameCount}` : ''}
            </span>
            {primaryViewLabel ? (
              <h3 className="order-3 w-full truncate text-center text-xs font-semibold text-gray-800 dark:text-gray-100 sm:order-none sm:w-auto sm:flex-1 sm:text-sm">
                {primaryViewLabel}
              </h3>
            ) : (
              <span className="order-3 w-full text-center text-xs text-gray-400 sm:order-none sm:w-auto sm:flex-1">
                No view assigned
              </span>
            )}
            <div className="relative z-10 flex shrink-0 items-center gap-1.5">
              {viewerFilter && onViewerFilterChange ? (
                <QuickInferenceViewerFilter
                  filter={viewerFilter}
                  onChange={onViewerFilterChange}
                  viewOptions={filterViewOptions}
                  structureOptions={filterStructureOptions}
                  matchedCount={matchedFrameCount ?? totalFrameCount ?? 0}
                  totalCount={totalUnfilteredFrameCount ?? totalFrameCount ?? 0}
                />
              ) : null}
              {showFramesToggle && onFramesPanelToggle ? (
                <button
                  type="button"
                  onClick={onFramesPanelToggle}
                  className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold transition-all ${
                    framesPanelOpen
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                      : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  <Film size={12} />
                  Frames
                  {totalFrameCount != null ? (
                    <span
                      className={`min-w-[16px] rounded px-1 py-0.5 text-[9px] font-black ${
                        framesPanelOpen ? 'bg-white/25 text-white' : 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {totalFrameCount}
                    </span>
                  ) : null}
                </button>
              ) : (
                <span className="text-xs tabular-nums text-gray-400">
                  {globalCurrentFrameIndex ?? entry.frameNumber + 1}
                  {totalFrameCount ? ` / ${totalFrameCount}` : ''}
                </span>
              )}
              <button
                type="button"
                onClick={() => setSidePanelOpen((open) => !open)}
                className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold transition-all ${
                  sidePanelOpen
                    ? 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
                    : 'border-blue-600 bg-blue-600 text-white shadow-sm'
                }`}
                title={sidePanelOpen ? 'Hide quality panel' : 'Show quality panel'}
              >
                {sidePanelOpen ? <PanelRightClose size={13} /> : <PanelRightOpen size={13} />}
                <span className="hidden sm:inline">Criteria</span>
              </button>
            </div>
          </div>

          {matchedFrameCount != null &&
          totalUnfilteredFrameCount != null &&
          matchedFrameCount < totalUnfilteredFrameCount ? (
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-violet-100 bg-violet-50 px-3 py-1.5 text-[10px] text-violet-700 dark:border-violet-900/40 dark:bg-violet-950/30 dark:text-violet-300">
              <span>
                Filter active — {matchedFrameCount} of {totalUnfilteredFrameCount} frames
              </span>
              {onClearFilter || onViewerFilterChange ? (
                <button
                  type="button"
                  onClick={() => {
                    if (onClearFilter) onClearFilter();
                    else onViewerFilterChange?.(EMPTY_PLAYGROUND_FILTER);
                  }}
                  className="font-semibold underline-offset-2 hover:underline"
                >
                  Clear
                </button>
              ) : null}
            </div>
          ) : null}

          {editable ? (
            <QuickInferenceAssignViewBar
              inferenceId={detail.id}
              frameKey={frameKey}
              frame={entry.frame}
              editable={editable}
              activeViewDefId={activeViewDefId}
              onActiveViewDefIdChange={setActiveViewDefId}
              onUpdated={onSaved}
            />
          ) : null}

          <div className="relative min-h-0 flex-1 bg-[#111827]">
            {emptyFilterMessage ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-sm font-semibold text-gray-300">No frames match this filter</p>
                <p className="max-w-xs text-xs text-gray-500">
                  Try another view/structure, or clear the filter to browse all frames again.
                </p>
                {onClearFilter ? (
                  <button
                    type="button"
                    onClick={onClearFilter}
                    className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-500"
                  >
                    Clear filter
                  </button>
                ) : null}
              </div>
            ) : canvasLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-600 border-t-blue-500" />
              </div>
            ) : !canvasReady || canvasFailed ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Frame unavailable
              </div>
            ) : (
              <QuickInferenceDrawingCanvas
                key={url}
                ref={drawingCanvasRef}
                images={images}
                initialCurrentImageId={imageId}
                onSave={handleSave}
                showSaveButton={editable}
                readOnly={!editable}
                totalFrameCount={totalFrameCount}
                globalCurrentFrameIndex={globalCurrentFrameIndex}
                onGlobalFrameIndexChange={onGlobalFrameIndexChange}
                viewCode={activeViewCode}
                hiddenCategoryIds={hiddenCategoryIds}
              />
            )}
            {saving ? (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 py-2 text-center text-xs text-white backdrop-blur-sm">
                Saving…
              </div>
            ) : null}

            {frameStrip ? (
              <aside
                className="absolute bottom-0 left-0 right-0 z-20 flex max-h-[min(50vh,360px)] flex-col border-t border-gray-200 bg-white transition-transform duration-200 ease-out dark:border-gray-700 dark:bg-gray-900 sm:max-h-[42%]"
                style={{
                  boxShadow: framesPanelOpen ? '0 -4px 24px rgba(0,0,0,0.12)' : 'none',
                  transform: framesPanelOpen ? 'translateY(0)' : 'translateY(100%)',
                }}
              >
                <div className="flex h-8 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 dark:border-gray-700 dark:bg-gray-800/50">
                  <div className="flex items-center gap-2">
                    <div className="h-3.5 w-0.5 rounded-full bg-blue-500" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      {totalFrameCount ?? 0} Frame{(totalFrameCount ?? 0) === 1 ? '' : 's'}
                    </span>
                  </div>
                  {onFramesPanelToggle ? (
                    <button
                      type="button"
                      onClick={onFramesPanelToggle}
                      className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    >
                      <X size={12} />
                    </button>
                  ) : null}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                  {frameStrip}
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </div>

      {sidePanelOpen ? (
        <aside
          className={`flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:w-[340px] xl:w-[360px] ${
            hasQualityViews
              ? 'min-h-0 max-lg:max-h-[min(45vh,420px)] max-lg:min-h-[200px] lg:self-stretch'
              : 'self-start'
          }`}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2.5 dark:border-gray-700">
            <div className="flex min-w-0 items-center gap-2">
              <div className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
              <h2 className="truncate text-xs font-semibold text-gray-800 dark:text-gray-200 sm:text-sm">
                Quality criteria
              </h2>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Zap size={14} className="text-amber-400" />
              <button
                type="button"
                onClick={() => setSidePanelOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                title="Hide panel"
              >
                <X size={12} />
              </button>
            </div>
          </div>
          <div
            className={`space-y-2.5 overflow-y-auto overscroll-contain p-2.5 ${
              hasQualityViews ? 'min-h-0 flex-1' : ''
            }`}
            style={{ scrollbarWidth: 'thin' }}
          >
            <QuickInferenceMeasurementsPanel frame={entry.frame} pixelSpacingMm={pixelSpacingMm} />
            <QuickInferenceCalibrationBar
              inferenceId={detail.id}
              onCalibrated={handleCalibrated}
              mode={needsCalibration ? 'required' : 'optional'}
              initialSpacingMm={pixelSpacingMm}
            />
            <QuickInferenceQualityPanel
              frame={entry.frame}
              activeViewCode={activeViewCode}
              hiddenCategoryIds={hiddenCategoryIds}
              onToggleCategoryVisibility={onToggleCategoryVisibility}
            />
          </div>
        </aside>
      ) : null}
    </div>
  );
}
